import ActionHandle from "./ActionHandle";
import AdminFooterButtons from "components/bo/admin-footer-buttons/AdminFooterButtons";
import Table from "core/admin-table/AdminTable";
import { ACTIONS, ENTITIES, VAR_TYPES } from "constants/constants";
import { FormattedMessage } from "react-intl";
import { ifValueSet } from "utils/inputValidators";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { boolToStringHelper, renderIf, translateHelper } from "utils/helper";
import {
  ENTITY_COLUMNS_MAP,
  createColElement,
  createEditableCell,
} from "utils/tableManagerHelper";
import { VIEW_DETAILS_COLUMNS } from "constants/bo-columns";
import PropTypes from "prop-types";

export const TableManager = ({
  action,
  entity,
  entityPerId,
  formattedEntity,
  list,
  handleCreate,
  handleDelete,
  handleEdit,
  handleUpload,
  uploadExchangeRate,
  navigateEdit,
  navigateView,
  isModal,
  inputs,
  uploadFileInputs,
  search,
  searchRole,
  watchFormInput,
  getYear,
  minDate,
  lang,
  contractualStatusList,
  contractualStatusSelected,
  setContractualStatusSelected,
  countryList,
  isRowDeletable,
}) => {
  const [formValues, setFormValues] = useState({});
  const [formInputs, setFormInputs] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const contractualStatusIdTableName =
    "admin_syntec_coef_page_table_name_contractual_status_id";
  const commonActions =
    action === ACTIONS.CREATE ||
    action === ACTIONS.EDIT ||
    action === ACTIONS.UPLOAD;
  useEffect(() => {
    if (action === ACTIONS.EDIT || action === ACTIONS.CREATE) {
      setFormInputs(inputs);
    }
    if (action === ACTIONS.UPLOAD) {
      setFormInputs(uploadFileInputs);
    }
  }, [action, inputs, uploadFileInputs]);
  useEffect(() => {
    if (commonActions) {
      setFormValues({});
    }
  }, [location.pathname]);

  useEffect(() => {
    if (action === ACTIONS.EDIT && formValues?.id !== entityPerId?.id) {
      setFormValues(entityPerId);
    }
  }, [entityPerId, formValues]);

  const updateData = (variable, value) => {
    if (variable.type === VAR_TYPES.NUMBER || variable.toBeParsed) {
      // An empty cell (blank dropdown option, cleared number input) stays
      // empty: parsing it would produce NaN, which `ifValueSet` reads as a
      // filled field and lets through a mandatory check as `null`.
      value = value === "" || value === null ? null : Number.parseFloat(value);
    }

    setFormValues({
      ...formValues,
      [variable.col]: value,
    });

    setFormInputs((old) => {
      return old.map((e) => {
        if (
          e.col === variable.col &&
          e.mandatory &&
          !ifValueSet(value, e.type)
        ) {
          return {
            ...e,
            isMissingValue: "red",
          };
        } else if (
          e.col === variable.col &&
          e.mandatory &&
          ifValueSet(value, e.type)
        ) {
          return { ...e, isMissingValue: null };
        } else return e;
      });
    });
  };

  useEffect(() => {
    if (
      action === ACTIONS.EDIT &&
      entityPerId &&
      entity === ENTITIES.DOCUMENT
    ) {
      setFormValues((prevValues) => ({
        ...prevValues,
        country_id: countryList?.find((c) => c.name === entityPerId.country_id)
          ?.id,
        file: entityPerId.uploaded_file_name,
      }));
    }
  }, [entityPerId, action]);

  const onSubmit = () => {
    const validatedInputs = formInputs.map((e) =>
      renderIf(
        e.mandatory && !ifValueSet(formValues[e.col]),
        {
          ...e,
          isMissingValue: "red",
        },
        e
      )
    );
    setFormInputs(validatedInputs);
    if (
      validatedInputs.filter((elm) => elm.isMissingValue === "red").length === 0
    ) {
      if (action === ACTIONS.CREATE) {
        handleCreate(formValues);
      } else if (action === ACTIONS.EDIT) {
        handleEdit(formValues);
      } else if (action === ACTIONS.UPLOAD) {
        handleUpload(formValues, navigate, location, uploadExchangeRate);
      }
    }
  };

  const formatContractualStatusSelected = (e) => {
    const selectedStatus = contractualStatusList.find((el1) =>
      Object.values(el1).some((el2) => el2 === e.target.value)
    );
    setContractualStatusSelected({
      id: selectedStatus.id,
      label: translateHelper({
        lang,
        fr: selectedStatus.fr,
        en: selectedStatus.en,
      }),
    });
  };

  const formatColumn = (columnList) => {
    return columnList?.map(({ id, defaultMessage, accessor, width }) => ({
      Header: (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <FormattedMessage id={id} defaultMessage={defaultMessage} />
          {id === contractualStatusIdTableName && (
            <select
              style={{ marginTop: "5px" }}
              value={contractualStatusSelected.label}
              onChange={(e) => {
                formatContractualStatusSelected(e);
              }}
              id={id}
            >
              {contractualStatusList?.map((el) => (
                <option
                  key={`option-${el?.id}`}
                  value={translateHelper({ lang, fr: el?.fr, en: el?.en })}
                >
                  {translateHelper({ lang, fr: el?.fr, en: el?.en })}
                </option>
              ))}
            </select>
          )}
        </div>
      ),
      accessor: accessor,
      width: width,
    }));
  };
  const manageColumns = () => {
    if (action === ACTIONS.DETAILS || commonActions)
      return formatColumn(VIEW_DETAILS_COLUMNS);
    else {
      const columns = ENTITY_COLUMNS_MAP[entity];
      return renderIf(columns, formatColumn(columns), []);
    }
  };

  const manageData = () => {
    switch (action) {
      case ACTIONS.DETAILS: {
        let displayedObject = formattedEntity || entityPerId;
        return Object.keys(displayedObject).map((e) => {
          return {
            col: createColElement({
              e: {
                col: e,
              },
              entity,
            }),
            value:
              typeof displayedObject[e] === "boolean"
                ? boolToStringHelper({ isTrue: displayedObject[e] })
                : displayedObject[e],
          };
        });
      }
      case ACTIONS.CREATE:
      case ACTIONS.UPLOAD:
      case ACTIONS.EDIT:
        return formInputs.map((e) => {
          return {
            col: createColElement({
              e,
              entity,
            }),
            value: renderIf(
              e.col === "id" && action === ACTIONS.EDIT,
              <>{formValues[e.col]}</>,
              createEditableCell({
                e,
                formValues,
                setFormValues,
                updateData,
                search,
                searchRole,
                watchFormInput,
                getYear,
                minDate,
              })
            ),
          };
        });
      default:
        return (list || []).map((e) => ({
          ...e,
          action: (
            <ActionHandle
              id={e.id}
              navigateView={() => navigateView(e.id)}
              handleDelete={() => handleDelete(e.id)}
              navigateEdit={() => navigateEdit(e.id)}
              canDelete={isRowDeletable ? isRowDeletable(e) : true}
            />
          ),
        }));
    }
  };
  const columnsParsed = useMemo(
    () => manageColumns(),
    [entity, action, entityPerId, formInputs, contractualStatusSelected, lang]
  );

  return (
    <>
      <div className="table-admin-profile">
        <Table
          id={`table-${entity}`}
          columns={columnsParsed}
          data={manageData()}
        />
      </div>
      <br />
      {!isModal && <AdminFooterButtons onSubmit={onSubmit} action={action} />}
    </>
  );
};

export default TableManager;

TableManager.propTypes = {
  action: PropTypes.string,
  entity: PropTypes.string,
  entityPerId: PropTypes.object,
  formattedEntity: PropTypes.object,
  list: PropTypes.array,
  handleCreate: PropTypes.func,
  handleDelete: PropTypes.func,
  handleEdit: PropTypes.func,
  navigateEdit: PropTypes.func,
  navigateView: PropTypes.func,
  isModal: PropTypes.bool,
  inputs: PropTypes.array,
  search: PropTypes.func,
  searchRole: PropTypes.func,
  watchFormInput: PropTypes.func,
  getYear: PropTypes.func,
  minDate: PropTypes.string,
  lang: PropTypes.string,
  contractualStatusList: PropTypes.array,
  contractualStatusSelected: PropTypes.object,
  setContractualStatusSelected: PropTypes.func,
  handleUpload: PropTypes.func,
  uploadExchangeRate: PropTypes.func,
  uploadFileInputs: PropTypes.object,
  countryList: PropTypes.array,
  isRowDeletable: PropTypes.func,
};
